import React, { useState, useRef, useEffect, HTMLAttributes } from 'react';
import ReactDOM from 'react-dom';
import { FiFilter, FiColumns, FiMenu, FiChevronDown, FiBarChart2, FiUsers, FiZap, FiCode, FiX, FiFileText, FiBell, FiShield, FiClipboard, FiDatabase, FiActivity, FiServer, FiSearch, FiPlay, FiCheckCircle } from 'react-icons/fi';
import { useAppConfig } from '../../context/AppConfigContext';
import { useDebug } from '../../components/debug/DebugLayout';
import { useUser } from '../../context/UserContext';
// LogoutApi import removed - will be re-added when logout button is integrated
import useHasRole, { FEATURE_PRIVACY_KPI_ENABLED } from '../../hooks/useHasRole';
import { usePermissions } from '../../hooks/usePermissions';
import '../../glass-styles.css'; // Import our dedicated CSS file

/**
 * Helper function to extract user initials from name
 * @param name - Full name (e.g., "John Doe")
 * @returns Initials (e.g., "JD") or "?" if name is invalid
 */
const getUserInitials = (name: string): string => {
  if (!name || typeof name !== 'string') return '?';

  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';

  if (parts.length === 1) {
    // Single name - return first letter
    return parts[0][0]?.toUpperCase() || '?';
  }

  // Multiple names - return first letter of first and last name
  const firstInitial = parts[0][0]?.toUpperCase() || '';
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase() || '';
  return `${firstInitial}${lastInitial}`;
};

// Add custom styles for preventing hover effects
const noHoverStyles = `
  .no-hover-effect,
  .no-hover-effect:hover,
  .no-hover-effect:active,
  .no-hover-effect:focus {
    background-color: transparent !important;
    outline: none !important;
    box-shadow: none !important;
  }
`;

// Add overlay styles
const overlayStyles = `
  .mobile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #1e293b;
    z-index: 9999;
    overflow: auto;
    opacity: 1 !important;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  }

  /* Ensure portal elements have the highest stacking context */
  body > .mobile-menu-overlay {
    z-index: 9999;
  }
`;

// Add fixed header styles to ensure it works across browsers
const fixedHeaderStyles = `
  /* Additional styles to ensure fixed header works correctly */
  .glass-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1000;
  }
`;

// No longer need the mock user data
// const USER = {
//   email: 'username@example.org',
//   avatar:
//     'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E',
// };

interface GlassHeaderProps extends HTMLAttributes<HTMLElement> {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  variant?: 'default' | 'minimal' | 'gradient';
  sticky?: boolean;
  _blur?: 'none' | 'sm' | 'md' | 'lg';
  isFilterOpen?: boolean;
  onFilterToggle?: () => void;
  hasActiveFilters?: boolean;
  hideFilter?: boolean;
}

const GlassHeader = React.forwardRef<HTMLDivElement, GlassHeaderProps>(
  (
    {
      className,
      title,
      subtitle,
      actions,
      variant = 'default',
      sticky = false,
      _blur = 'md',
      isFilterOpen = false,
      onFilterToggle = () => {},
      hasActiveFilters = false,
      hideFilter = false,
      ...props
    },
    ref
  ) => {
    const { filterPosition, setFilterPosition, themeMode } = useAppConfig();
    const { showDebug, toggleDebug } = useDebug();
    const { activePersona } = useUser();
    const { canAccessPrivacyKpis: rbacCanAccessPrivacyKpis } = usePermissions();

    // RBAC Phase 4: Privacy KPI access now sourced from RBAC permissions (replaces TEMP_ADMIN_USER_IDS)
    const canAccessPrivacyKpis = FEATURE_PRIVACY_KPI_ENABLED && rbacCanAccessPrivacyKpis;
    // CC-211-AUDIT Phase 2I: Credit Audit is Platform Admin only (mirrors server gate)
    const canAccessCreditAudit = useHasRole(['Platform Admin']);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
    const filterMenuRef = useRef<HTMLDivElement>(null);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
    // New state for mobile menu
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    // Close menus when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
          setIsFilterMenuOpen(false);
        }
        if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
          setIsUserMenuOpen(false);
        }
        if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) && isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobileMenuOpen]);

    // Prevent body scrolling when mobile menu is open
    useEffect(() => {
      if (isMobileMenuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      
      return () => {
        document.body.style.overflow = '';
      };
    }, [isMobileMenuOpen]);

    // Function to navigate to the main list view
    const goToMainView = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation(); // Stop event propagation
      // Use history.pushState to update URL without page reload
      const url = new URL(window.location.href);
      url.search = ''; // Remove all query parameters
      window.history.pushState({}, '', url.toString());
      
      // Dispatch a custom event to notify App component of the change
      window.dispatchEvent(new Event('popstate'));
      // Close mobile menu after navigation
      setIsMobileMenuOpen(false);
    };
    
    // Function to navigate between views without page reload
    const navigateToView = (e: React.MouseEvent, viewName: string) => {
      e.preventDefault();
      e.stopPropagation(); // Stop event propagation

      // Use history.pushState to update URL without page reload
      const url = new URL(window.location.href);
      url.searchParams.set('view', viewName);
      window.history.pushState({}, '', url.toString());

      // Dispatch a custom event to notify App component of the change
      window.dispatchEvent(new Event('popstate'));
      // Close mobile menu after navigation
      setIsMobileMenuOpen(false);
      // D2: Close user/admin dropdown after navigation
      setIsUserMenuOpen(false);
    };
    
    // Get current view from URL
    const getCurrentView = () => {
      const params = new URLSearchParams(window.location.search);
      return params.get('view') || 'members';
    };

    const currentView = getCurrentView();

    // Build the final class name for the header
    const isDarkVariant = variant === 'gradient';
    const isLightTheme = themeMode === 'light';
    const isDarkThemeWithGradient = isDarkVariant && !isLightTheme;
    
    // Determine the final class for the header container
    const headerClassName = `glass-header ${variant} ${sticky ? 'sticky' : ''} ${isLightTheme && variant === 'gradient' ? 'light-mode' : ''} ${className || ''}`.trim();
    
    // Create mobile menu content
    const renderMobileMenu = () => {
      if (!isMobileMenuOpen) return null;
      
      // Get the DOM node to mount the portal
      const portalNode = document.body;
      
      // Only render if we have a portal node and the component is mounted
      if (!portalNode) return null;
      
      return ReactDOM.createPortal(
        <div className="mobile-menu-overlay">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center">
              <img 
                src="/VS-Logo_clear-bg.svg" 
                alt="VeraScore Logo" 
                style={{ 
                  height: '28px',
                  filter: 'brightness(0) invert(1)'
                }} 
              />
              <span className="ml-2 font-bold text-white">Lender's Dashboard</span>
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(false);
              }}
              className="p-2 rounded-md hover:bg-white/10 text-white"
            >
              <FiX size={22} />
            </button>
          </div>
          
          <div className="p-4">
            <nav className="flex flex-col gap-2">
              <a
                href="#"
                onClick={(e) => navigateToView(e, 'members')}
                className={`flex items-center gap-3 px-4 py-3 rounded-md ${
                  currentView === 'members' || currentView === '' 
                    ? 'bg-white/15 text-white font-medium' 
                    : 'text-white/90 hover:bg-white/5'
                }`}
              >
                <FiUsers size={20} />
                <span className="font-medium">Member Details</span>
              </a>
              
              <a
                href="#"
                onClick={(e) => navigateToView(e, 'actions')}
                className={`flex items-center gap-3 px-4 py-3 rounded-md ${
                  currentView === 'actions' 
                    ? 'bg-white/15 text-white font-medium' 
                    : 'text-white/90 hover:bg-white/5'
                }`}
              >
                <FiZap size={20} />
                <span className="font-medium">VS Actions</span>
              </a>
              
              <a
                href="#"
                onClick={(e) => navigateToView(e, 'insights')}
                className={`flex items-center gap-3 px-4 py-3 rounded-md ${
                  currentView === 'insights' 
                    ? 'bg-white/15 text-white font-medium' 
                    : 'text-white/90 hover:bg-white/5'
                }`}
              >
                <FiBarChart2 size={20} />
                <span className="font-medium">VS Insights</span>
              </a>
              
              <a
                href="#"
                onClick={(e) => navigateToView(e, 'settings')}
                className={`flex items-center gap-3 px-4 py-3 rounded-md ${
                  currentView === 'settings' 
                    ? 'bg-white/15 text-white font-medium' 
                    : 'text-white/90 hover:bg-white/5'
                }`}
              >
                <FiFileText size={20} />
                <span className="font-medium">Reports</span>
              </a>
            </nav>
            
            <div className="border-t border-white/10 my-4 pt-4">
              <div className="px-4 py-2 text-sm text-white/70">Current User</div>
              <div className="flex items-center gap-3 px-4 py-2">
                {/* BUG-LA-003: Replace broken image with colored circle avatar */}
                <div
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-purple flex items-center justify-center text-white text-base font-bold shadow-sm"
                  title={activePersona.name}
                >
                  {getUserInitials(activePersona.name)}
                </div>
                <div>
                  <div className="font-medium text-white">{activePersona.name}</div>
                  <div className="text-xs text-white/70">{activePersona.email}</div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        portalNode
      );
    };

    return (
      <header
        ref={ref}
        className={headerClassName}
        {...props}
      >
        <style dangerouslySetInnerHTML={{ __html: noHoverStyles + overlayStyles + fixedHeaderStyles }} />
        <div
          className="max-w-7xl mx-auto px-3 sm:px-4 h-16 flex items-center justify-between relative"
        >
          {/* Hamburger menu button - only visible on mobile - positioned on left */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            className="md:hidden p-2 rounded-md"
            style={{
              border: isDarkThemeWithGradient ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
              background: isDarkThemeWithGradient ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            }}
          >
            <FiMenu size={22} style={{ color: '#27555E' }} />
          </button>
          
          {/* Left section: Logo - centered on mobile, left-aligned on desktop */}
          <div className="flex items-center pointer-events-auto md:flex-none absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0">
            {/* Logo with link to main list view */}
            <a
              href="#"
              onClick={goToMainView}
              className="no-underline transition-colors duration-200 flex items-center gap-2"
            >
              <img
                src="/VS-Logo_clear-bg.svg"
                alt="VeraScore Logo"
                style={{
                  height: '40px',
                  width: '40px',
                  filter: 'none'
                }}
              />

              {/* VeraScore Lenders App Logo */}
              <img
                src="/favicon.png"
                alt="VeraScore Lenders App"
                style={{
                  height: '32px',
                  width: 'auto'
                }}
              />
              
              {/* Dashboard text - hidden on mobile, visible on desktop */}
              <span className="hidden md:inline-block whitespace-nowrap font-bold dashboard-title" style={{ color: '#27555E', fontSize: '1.125rem' }}>
                Lender's Dashboard
              </span>
            </a>
          </div>

          {/* Navigation links - Centered in header - DESKTOP ONLY */}
          <div className="hidden md:flex flex-1 justify-center">
            <div className="flex gap-2 md:gap-8"> {/* Responsive spacing between nav items */}
              <a
                href="#"
                onClick={(e) => navigateToView(e, 'members')}
                className="flex items-center gap-1 px-2 py-2 md:px-3 md:py-2 rounded-md text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: currentView === 'members' || currentView === '' ? 'rgba(39, 85, 94, 0.1)' : 'transparent',
                  color: currentView === 'members' || currentView === '' ? '#27555E' : '#000000'
                }}
              >
                <FiUsers size={16} style={{ color: '#27555E' }} />
                <span>Member Details</span>
              </a>
              <a
                href="#"
                onClick={(e) => navigateToView(e, 'actions')}
                className="flex items-center gap-1 px-2 py-2 md:px-3 md:py-2 rounded-md text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: currentView === 'actions' ? 'rgba(39, 85, 94, 0.1)' : 'transparent',
                  color: currentView === 'actions' ? '#27555E' : '#000000'
                }}
              >
                <FiZap size={16} style={{ color: '#27555E' }} />
                <span>VS Actions</span>
              </a>
              <a
                href="#"
                onClick={(e) => navigateToView(e, 'insights')}
                className="flex items-center gap-1 px-2 py-2 md:px-3 md:py-2 rounded-md text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: currentView === 'insights' ? 'rgba(39, 85, 94, 0.1)' : 'transparent',
                  color: currentView === 'insights' ? '#27555E' : '#000000'
                }}
              >
                <FiBarChart2 size={16} style={{ color: '#27555E' }} />
                <span>VS Insights</span>
              </a>
            </div>
          </div>

          {/* Right section: Filter button, Notifications, and User info */}
          <div className="flex items-center gap-2 sm:gap-4 justify-end ml-auto">
            {/* Combined Filter Menu - only show if hideFilter is false */}
            {!hideFilter && (
              <div ref={filterMenuRef} className="relative">
                <button
                  onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                  className="relative flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md text-slate-700 border border-slate-300"
                  aria-haspopup="true"
                >
                  <FiFilter size={16} style={{ color: '#27555E' }} />
                  <span className="hidden sm:inline">Filter</span>
                  {hasActiveFilters && (
                    <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-600 text-white">
                      Active
                    </span>
                  )}
                  <FiChevronDown
                    size={14}
                    className={`transform transition-transform duration-200 ${
                      isFilterMenuOpen ? 'rotate-180' : 'rotate-0'
                    }`}
                    style={{ color: '#27555E' }}
                  />
                </button>

                {/* Filter Dropdown Menu */}
                {isFilterMenuOpen && (
                  <div
                    className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg z-10 border ${
                      isDarkThemeWithGradient 
                        ? 'bg-slate-800 border-white/20' 
                        : 'bg-white border-slate-200'
                    }`}
                  >
                    {/* Filter Position Options */}
                    <button
                      onClick={() => {
                        setFilterPosition('top');
                        setIsFilterMenuOpen(false);
                        if (!isFilterOpen) {
                          onFilterToggle(); // Open filters if they're closed
                        }
                      }}
                      className={`flex items-center w-full px-4 py-2 text-sm rounded-md ${
                        filterPosition === 'top' ? 'text-blue-700 bg-blue-100/70 font-semibold' : 'text-slate-700 font-medium'
                      }`}
                    >
                      <FiMenu size={16} className="mr-2" style={{ color: '#27555E' }} />
                      <span>Top Filters</span>
                    </button>

                    <button
                      onClick={() => {
                        setFilterPosition('left');
                        setIsFilterMenuOpen(false);
                        if (!isFilterOpen) {
                          onFilterToggle(); // Open filters if they're closed
                        }
                      }}
                      className={`flex items-center w-full px-4 py-2 text-sm rounded-md ${
                        filterPosition === 'left' ? 'text-blue-700 bg-blue-100/70 font-semibold' : 'text-slate-700 font-medium'
                      }`}
                    >
                      <FiColumns size={16} className="mr-2" style={{ color: '#27555E' }} />
                      <span>Side Filters</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Notification Bell */}
            <button
              onClick={(e) => navigateToView(e, 'notifications')}
              className="relative p-2 rounded-md text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Notifications"
            >
              <FiBell size={20} style={{ color: '#27555E' }} />
              {/* Unread indicator - you can add conditional logic here */}
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User info */}
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 bg-transparent border-none cursor-pointer"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                {/* BUG-LA-003: Replace broken image with colored circle avatar */}
                <div
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-blue to-brand-purple flex items-center justify-center text-white text-sm font-bold shadow-sm"
                  title={activePersona.name}
                >
                  {getUserInitials(activePersona.name)}
                </div>
                <span
                  className="hidden sm:block text-sm font-medium"
                  style={{
                    color: '#000000',
                  }}
                >
                  {activePersona.name}
                </span>
                <FiChevronDown
                  size={14}
                  className={`transform transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : 'rotate-0'}`}
                  style={{
                    color: '#9CA3AF',
                  }}
                />
              </button>

              {/* User Dropdown Menu */}
              {isUserMenuOpen && (
                <div
                  className={`absolute right-0 mt-2 w-48 sm:w-64 rounded-md shadow-lg z-50 border ${
                    isDarkThemeWithGradient 
                      ? 'bg-slate-800 border-white/20' 
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div
                    className="py-2 px-4 border-b border-slate-200/20"
                  >
                    Signed in as
                    <br />
                    <span className="font-medium">{activePersona.name}</span>
                    <br />
                    <span className="text-xs opacity-70">{activePersona.email}</span>
                    <br />
                    <span className="text-xs opacity-50">{activePersona.description}</span>
                  </div>

                  {/* Action items */}
                  <div className="py-1">
                    <button 
                      className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                      onClick={() => {
                        toggleDebug();
                        setIsUserMenuOpen(false);
                      }}
                    >
                      <FiCode size={16} className="mr-2" />
                      Debug Mode {showDebug ? 'On' : 'Off'}
                    </button>
                    
                    <div className="border-t border-slate-200/20 my-1"></div>
                    
                    <button 
                      className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                      onClick={(e) => navigateToView(e, 'profile')}
                    >
                      <FiUsers size={16} className="mr-2" />
                      Profile
                    </button>
                    
                    <button 
                      className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                      onClick={(e) => navigateToView(e, 'settings')}
                    >
                      <FiFileText size={16} className="mr-2" />
                      Reports
                    </button>

                    {/* PLAID-69: Data Health Dashboard - Admin Only */}
                    {canAccessPrivacyKpis && (
                      <>
                        <div className="border-t border-slate-200/20 my-1"></div>
                        <button
                          className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                          onClick={(e) => navigateToView(e, 'admin/data-health')}
                        >
                          <FiDatabase size={16} className="mr-2" />
                          Data Health
                        </button>
                      </>
                    )}

                    {/* LAPI v1.1: Endpoint Management - Admin Only */}
                    {canAccessPrivacyKpis && (
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                        onClick={(e) => navigateToView(e, 'admin/endpoint-management')}
                      >
                        <FiServer size={16} className="mr-2" />
                        Endpoint Management
                      </button>
                    )}

                    {/* VS-FI-INVESTIGATION: Unified FI Investigation - Admin Only */}
                    {canAccessPrivacyKpis && (
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                        onClick={(e) => navigateToView(e, 'admin/plaid-connection-monitor')}
                      >
                        <FiSearch size={16} className="mr-2" />
                        FI Investigation
                      </button>
                    )}

                    {/* PRIV-25.2: Privacy KPI Dashboard - Admin Only */}
                    {canAccessPrivacyKpis && (
                      <>
                        <div className="border-t border-slate-200/20 my-1"></div>
                        <button
                          className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                          onClick={(e) => navigateToView(e, 'admin/privacy-kpis')}
                        >
                          <FiShield size={16} className="mr-2" />
                          Privacy KPIs
                        </button>
                      </>
                    )}

                    {/* CC-211-AUDIT Phase 2I: Credit Audit - Platform Admin Only */}
                    {canAccessCreditAudit && (
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsUserMenuOpen(false);
                          const url = new URL(window.location.href);
                          url.searchParams.set('view', 'admin/privacy-kpis');
                          url.searchParams.set('tab', 'credit-audit');
                          window.history.pushState({}, '', url.toString());
                          window.dispatchEvent(new Event('popstate'));
                        }}
                      >
                        <FiClipboard size={16} className="mr-2" />
                        Credit Audit
                      </button>
                    )}

                    {/* #78-C Phase 1: Score Audit - Platform Admin Only */}
                    {canAccessCreditAudit && (
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                        onClick={(e) => navigateToView(e, 'admin/scoring-audit')}
                      >
                        <FiActivity size={16} className="mr-2" />
                        Financial Health Score Audit
                      </button>
                    )}

                    {/* MEMBER-ONBOARDING-DATA-READINESS-REPORT-1 Phase 2.5: Onboarding Readiness - Platform Admin Only (D1: shortened label to avoid wrap) */}
                    {canAccessCreditAudit && (
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                        onClick={(e) => navigateToView(e, 'admin/onboarding-readiness')}
                      >
                        <FiClipboard size={16} className="mr-2" />
                        Onboarding Readiness
                      </button>
                    )}

                    {/* DASH-RUN-001-V1A Phase 6A: Intra-Month Score Trigger - Platform Admin Only */}
                    {canAccessCreditAudit && (
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                        onClick={(e) => navigateToView(e, 'admin/intra-month-trigger')}
                      >
                        <FiPlay size={16} className="mr-2" />
                        Intra-Month Score Trigger
                      </button>
                    )}

                    {/* BUG-23: Official Month Update - Platform Admin Only */}
                    {canAccessCreditAudit && (
                      <button
                        className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                        onClick={(e) => navigateToView(e, 'admin/official-month-update')}
                      >
                        <FiCheckCircle size={16} className="mr-2" />
                        Official Month Update
                      </button>
                    )}

                    <button
                      className="flex w-full items-center px-4 py-2 text-sm hover:bg-slate-100/10"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsUserMenuOpen(false);
                        // Navigate to logout view
                        window.history.pushState({}, '', '?view=logout');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Custom actions passed to the header */}
            {actions && (
              <div className="flex items-center gap-2">{actions}</div>
            )}
          </div>
        </div>
        
        {/* Render mobile menu using Portal */}
        {renderMobileMenu()}
      </header>
    );
  }
);

GlassHeader.displayName = 'GlassHeader';

export { GlassHeader };
